import { Box, Typography, Button } from '@mui/material';
import { SupervisorAccount, Logout, Block } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks/useAppStore';
import { useStopImpersonationMutation } from '../../store/api/authApi';
import { setCredentials } from '../../features/auth/authSlice';
import { baseApi } from '../../store/api/baseApi';

export function ImpersonationBanner() {
  const user = useAppSelector((s) => s.auth.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [stopImpersonation, { isLoading }] = useStopImpersonationMutation();

  if (!user?.impersonatedBy) return null;

  /**
   * El usuario al que se entró está DADO DE BAJA.
   *
   * Desde el 03/09/2026 un admin puede entrar como alguien bloqueado sin reactivarlo
   * (antes había que activarlo, y esa activación quedaba puesta). El banner tiene que
   * decirlo: si no, el admin ve un CRM normal y da por hecho que está mirando a alguien
   * que sigue trabajando. Cambia lo que interpreta de todo lo que ve — la cartera está
   * inactiva, los avisos despublicados, las consultas sin atender.
   *
   * `isActive === false` explícito, no `!user.isActive`: si el campo no viene (una sesión
   * vieja rehidratada antes de este cambio) no se afirma nada.
   */
  const deBaja = user.isActive === false;

  const handleStop = async () => {
    try {
      const result = await stopImpersonation().unwrap();
      dispatch(setCredentials({ user: result.user, accessToken: result.accessToken }));
      dispatch(baseApi.util.resetApiState());
      navigate('/dashboard', { replace: true });
    } catch {
      // No tragar el error: si el "volver" falla (raro con el stop idempotente del
      // backend), recargamos para reconciliar la sesión real (/auth/me) y NO quedar
      // trabados en el banner de impersonation.
      window.location.assign('/dashboard');
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 56, left: 0, right: 0,
        /**
         * ⛔ POR ENCIMA DEL SIDEBAR. El sidebar se dibuja en `theme.zIndex.drawer + 2`
         * (`Sidebar.tsx`), así que con el 1200 fijo que había acá le tapaba el borde
         * izquierdo al cartel: con dos líneas todavía se leía, y con tres —el aviso de
         * usuario dado de baja— la tercera quedaba abajo del menú. (03/09/2026, visto en
         * pantalla por el user)
         *
         * Va con el token del tema y no con un número: si mañana el sidebar sube, sube
         * con él en vez de volver a taparse en silencio.
         */
        zIndex: (t) => t.zIndex.drawer + 3,
        background: deBaja
          ? 'linear-gradient(90deg, #450a0a 0%, #7f1d1d 50%, #450a0a 100%)'
          : 'linear-gradient(90deg, #451a03 0%, #78350f 50%, #451a03 100%)',
        color: '#fff',
        px: 3, py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
      }}
    >
      {deBaja ? <Block sx={{ fontSize: 20 }} /> : <SupervisorAccount sx={{ fontSize: 20 }} />}
      <Box flex={1}>
        <Typography fontSize={13} fontWeight={700}>
          {deBaja
            ? 'Modo impersonation · este usuario NO está activo en el CRM'
            : 'Modo impersonation activo'}
        </Typography>
        <Typography fontSize={11} sx={{ opacity: 0.9 }}>
          Estás viendo el CRM como <strong>{user.name} {user.lastName}</strong> · {user.email} · Todas las acciones quedan auditadas.
        </Typography>
        {deBaja && (
          <Typography fontSize={11} sx={{ opacity: 0.95, mt: 0.25 }}>
            Está dado de baja: no puede iniciar sesión, su cartera quedó inactiva y sus avisos
            despublicados. No hace falta reactivarlo para mirar.
          </Typography>
        )}
      </Box>
      <Button
        size="small"
        variant="contained"
        startIcon={<Logout sx={{ fontSize: '16px !important' }} />}
        onClick={handleStop}
        disabled={isLoading}
        sx={{
          bgcolor: '#f59e0b',
          color: '#000',
          fontWeight: 900,
          border: '1px solid rgba(245, 158, 11, 0.5)',
          boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
          '&:hover': {
            bgcolor: '#fbbf24',
            borderColor: '#fbbf24',
          },
        }}
      >
        {isLoading ? 'Volviendo...' : 'Volver a mi admin'}
      </Button>
    </Box>
  );
}
